import { Loading } from "../../app/Status";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  MODULE_LABELS,
  ORG_MODULE_STATUS_LABELS,
  ORG_STATUS_LABELS,
  TRIAL_EXTEND_DAYS,
  type ModuleKey,
  type SupportAccountDto,
  type SupportActionBody,
  type SupportOrgHit,
  type TicketDetail,
} from "@klantpunt/shared";
import { api } from "../../lib/api";
import { useMe } from "../../lib/auth";
import { useToast } from "../../lib/toast";
import { errMsg, inboxKey, ticketKey } from "./api";

const fmtDay = (iso: string) =>
  new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" });
export const accountKey = (ticketId: string) => ["support", "account", ticketId] as const;

/**
 * Account van de afzender in het zijpaneel (docs/HELPCENTER.md §4.3, besluit 5): organisatie en modules,
 * "Koppelen aan organisatie" en de beheeracties voor de supportbeheerder.
 */
export function AccountPanel({ t }: { t: TicketDetail }) {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({
    queryKey: accountKey(t.id),
    queryFn: () => api<SupportAccountDto>(`/support/tickets/${t.id}/account`),
  });
  const [busy, setBusy] = useState(false);
  const admin = me?.support === "support_admin";
  const a = q.data;

  async function post(path: string, body: unknown, done: string) {
    setBusy(true);
    try {
      const d = await api<SupportAccountDto>(`/support/tickets/${t.id}${path}`, { method: "POST", body });
      qc.setQueryData(accountKey(t.id), d);
      void qc.invalidateQueries({ queryKey: ticketKey(t.id) });
      void qc.invalidateQueries({ queryKey: inboxKey });
      toast(done);
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  }
  const act = (body: SupportActionBody, done: string) => post("/actions", body, done);

  return (
    <section className="sp-side panel" aria-label="Account">
      <h3>Account</h3>
      {q.isPending && <Loading small />}
      {a && !a.org && (
        <LinkOrg busy={busy} onPick={(o) => void post("/org", { orgId: o.id }, `Gekoppeld aan ${o.name}`)} />
      )}
      {a?.org && (
        <>
          <dl className="sp-facts sp-inline">
            <div>
              <dt>Organisatie</dt>
              <dd>{a.org.name}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>
                {ORG_STATUS_LABELS[a.org.status]}
                {a.org.trialEndsAt && a.org.status !== "active" ? ` tot ${fmtDay(a.org.trialEndsAt)}` : ""}
              </dd>
            </div>
            {a.org.modules.map((m) => (
              <div key={m.module}>
                <dt>{MODULE_LABELS[m.module]}</dt>
                <dd>
                  {ORG_MODULE_STATUS_LABELS[m.status]}
                  {m.status === "trial" && m.trialEndsAt ? ` tot ${fmtDay(m.trialEndsAt)}` : ""}
                </dd>
              </div>
            ))}
          </dl>
          {admin && (
            <div className="sp-acts">
              <h4 className="small">Beheeracties</h4>
              <button
                type="button"
                className="btn small"
                disabled={busy || !a.mailEnabled || !a.requesterHasAccess}
                title={
                  !a.mailEnabled
                    ? "E-mail staat niet aan op de server"
                    : !a.requesterHasAccess
                      ? "De afzender heeft geen account met toegang"
                      : undefined
                }
                onClick={() => void act({ action: "login_link" }, "Inloglink verstuurd")}
              >
                Inloglink opnieuw sturen
              </button>
              <ModuleAction
                modules={a.org.modules.filter((m) => m.status !== "active").map((m) => m.module)}
                busy={busy}
                onSave={(module, status) =>
                  void act(
                    { action: "module", module: module as Exclude<ModuleKey, "buildings">, status },
                    `${MODULE_LABELS[module]} aangezet`,
                  )
                }
              />
              {a.org.trialEndsAt && (a.org.status === "trial" || a.org.status === "read_only") && (
                <label className="field small">
                  <span>Proefperiode verlengen</span>
                  <select
                    value=""
                    disabled={busy}
                    onChange={(e) =>
                      e.target.value &&
                      void act(
                        { action: "trial", days: Number(e.target.value) as (typeof TRIAL_EXTEND_DAYS)[number] },
                        "Proefperiode verlengd",
                      )
                    }
                  >
                    <option value="">Kies…</option>
                    {TRIAL_EXTEND_DAYS.map((d) => (
                      <option key={d} value={d}>
                        Met {d} dagen
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <p className="muted small">Elke actie komt in de audit-log van de organisatie en in het gesprek.</p>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function ModuleAction({
  modules,
  busy,
  onSave,
}: {
  modules: ModuleKey[];
  busy: boolean;
  onSave: (module: ModuleKey, status: "active" | "trial") => void;
}) {
  const [module, setModule] = useState<ModuleKey | "">("");
  const [status, setStatus] = useState<"active" | "trial">("trial");
  if (!modules.length) return null;
  return (
    <div className="sp-modact">
      <label className="field small">
        <span>Module aanzetten</span>
        <select value={module} onChange={(e) => setModule(e.target.value as typeof module)}>
          <option value="">Kies een module</option>
          {modules.map((m) => (
            <option key={m} value={m}>
              {MODULE_LABELS[m]}
            </option>
          ))}
        </select>
      </label>
      <label className="field small">
        <span className="sr-only">Hoe</span>
        <select value={status} onChange={(e) => setStatus(e.target.value as "active" | "trial")} aria-label="Hoe">
          <option value="trial">Als proef (30 dagen)</option>
          <option value="active">Aan</option>
        </select>
      </label>
      <button
        type="button"
        className="btn small"
        disabled={busy || !module}
        onClick={() => module && onSave(module, status)}
      >
        Aanzetten
      </button>
    </div>
  );
}

function LinkOrg({ busy, onPick }: { busy: boolean; onPick: (o: SupportOrgHit) => void }) {
  const [q, setQ] = useState("");
  const term = q.trim();
  const hits = useQuery({
    queryKey: ["support", "orgs", term],
    queryFn: () => api<SupportOrgHit[]>(`/support/orgs?q=${encodeURIComponent(term)}`),
    enabled: term.length >= 2,
    placeholderData: (prev) => prev,
  });
  return (
    <div className="sp-linkorg">
      <p className="muted small">Niet gekoppeld aan een organisatie.</p>
      <label className="field small">
        <span>Koppelen aan organisatie</span>
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek op naam" />
      </label>
      {term.length >= 2 && (
        <ul className="sp-picklist">
          {hits.data?.length === 0 && <li className="muted small">Niets gevonden.</li>}
          {(hits.data ?? []).map((o) => (
            <li key={o.id}>
              <button type="button" className="linkbtn" disabled={busy} onClick={() => onPick(o)}>
                {o.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
